import { Avatar, Box, Stack, Typography } from '@mui/material'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import { IconButton, TextInputField, TextInputFieldProps } from 'utoniq-ui'

import { BottomSafeArea } from '@/components/layouts'
import { useImageEditor } from '@/components/organisms/imageEditor'
import { styles, styleSets } from '@/components/styles'
import { handleError, useMessage, useTranslation } from '@/core'
import { CellFrame, OrganismFrame } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'
import { usePrivateAction } from '@/lib/privateAction'

export type ReplyFormRef = {
  focus: () => void
}

export type ReplyFormProps = {
  visibleImage?: boolean
  onReply: (values: ReplyFormValues) => Promise<void>
  onSelectImage?: () => Promise<{ uri: string }>
  autoFocus?: boolean
  userId: string
  replyToDisplayName?: string | null
  onRemoveReplyToReply?: () => void
  maxWidth?: number | string
  isConsole?: boolean
  /**
   * 単一入力モード
   * true の場合
   * 画像のみ もしくは コメントのみ のどちらか一方のみ送信可能
   * false または未指定の場合
   * コメントのみ もしくは コメント&画像の両方 が送信可能
   */
  singleInputMode?: boolean
}

export type ReplyFormValues = {
  content: string
  resources?: { tmpPath: string }[] | null | undefined
}

const schema: UseFormProps['schema'] = o => ({
  content: o.string().max(200),
  resources: o
    .array()
    .items(
      o.object({
        tmpPath: o.string().allow('').optional(),
      })
    )
    .allow(null)
    .optional(),
})

const schemaExclusiveMode: UseFormProps['schema'] = o => ({
  content: o.when('resources', {
    is: null,
    then: o.string().max(200).required(),
    otherwise: o.string().valid('').max(0),
  }),
  resources: o
    .array()
    .items(
      o.object({
        tmpPath: o.string().allow('').optional(),
      })
    )
    .allow(null)
    .optional(),
})

const defaultValues: ReplyFormValues = {
  content: '',
  resources: null,
}

export const ReplyForm = forwardRef<ReplyFormRef, ReplyFormProps>(
  (
    {
      visibleImage,
      onReply,
      autoFocus = false,
      userId,
      replyToDisplayName,
      onRemoveReplyToReply,
      maxWidth = '600px',
      isConsole = false,
      singleInputMode = false,
    },
    ref
  ) => {
    const { showError } = useMessage()
    const { t } = useTranslation('postDetail')
    const { isLoggedIn, handleLoginModal } = usePrivateAction()
    const formAreaRef = useRef<HTMLDivElement>(null)
    const {
      handleSubmit,
      register,
      reset,
      watch,
      setValue,
      formState: { errors, isValid, isSubmitting, touchedFields, dirtyFields },
      setFocus,
      setError,
    } = useForm<ReplyFormValues>({
      schema: singleInputMode ? schemaExclusiveMode : schema,
      defaultValues,
    })
    const watchContent = watch('content')

    const color = useMemo(() => {
      if (!isLoggedIn) {
        return 'primary'
      }
      return !isValid || isSubmitting ? 'inherit' : 'primary'
    }, [isLoggedIn, isValid, isSubmitting])

    useImperativeHandle(ref, () => ({
      focus() {
        setFocus('content')
      },
    }))

    const {
      ImageEditModal,
      ModalProps,
      handleChoseFile,
      handleRemoveFile,
      filePathAfterCrop,
    } = useImageEditor({
      userId,
      callback: async ({ tmpPath }) => {
        setValue('resources', [{ tmpPath }], {
          shouldDirty: true,
          shouldValidate: true,
        })
      },
    })

    // 画像未選択、もしくは単一入力モード時にコメントが入力されていない場合は画像選択可能
    const canChooseImage =
      !filePathAfterCrop || (singleInputMode && watchContent.length !== 0)

    // 通常モード、もしくは単一入力モード時に画像が選択されていない場合はコメント編集可能
    const canEditComment = !singleInputMode || !filePathAfterCrop

    //Handle resizing the height of the footer input
    useEffect(() => {
      const observer = new ResizeObserver(entries => {
        for (const entry of entries) {
          const node = document.getElementById('bottomMargin')
          if (node?.style) {
            node.style.height = `calc(${entry.contentRect.height ?? 0}px + ${
              styles.safeAreaBottom
            })`
          }
        }
      })
      if (formAreaRef.current) {
        observer.observe(formAreaRef.current)
      }
      return () => observer.disconnect()
    }, [])

    const onSubmit = handleSubmit(async values => {
      await onReply(values)
        .then(() => {
          reset()
          handleRemoveFile()
        })
        .catch(error =>
          handleError<ReplyFormValues>(error, setError, showError)
        )
    })

    return (
      <OrganismFrame
        sx={{
          bgcolor: 'grey.50',
          maxWidth: maxWidth,
          margin: 'auto',
        }}
      >
        <BottomSafeArea sx={{ bgcolor: 'grey.50' }}>
          <Box ref={formAreaRef}>
            {!!replyToDisplayName && (
              <Stack
                flex={1}
                direction="row"
                sx={{ bgcolor: 'grey.100', px: 4, minHeight: 28 }}
                alignItems="center"
              >
                <Typography variant="overline" color="text.hint">
                  {t('reply')}
                </Typography>
                <Stack flex={1} sx={{ minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    sx={{ ...styleSets.lineCamp(1) }}
                  >
                    {replyToDisplayName}
                  </Typography>
                </Stack>
                {onRemoveReplyToReply && (
                  <IconButton.Close
                    size="xSmall"
                    onClick={onRemoveReplyToReply}
                    sx={{ mr: -2 }}
                  />
                )}
              </Stack>
            )}
            <Form onSubmit={onSubmit} sx={{ bgcolor: 'transparent' }}>
              <Stack
                direction="row"
                spacing={4}
                sx={{ px: 5, overflowX: 'auto' }}
              >
                {filePathAfterCrop && (
                  <Box sx={{ position: 'relative', pt: 4, pb: 1 }}>
                    <Avatar
                      variant="square"
                      src={filePathAfterCrop}
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        img: { objectFit: 'contain' },
                      }}
                    />
                    <IconButton.Close
                      size="xSmall"
                      bgcolor="grey.100"
                      sx={{ position: 'absolute', top: 4, right: -12 }}
                      onClick={handleRemoveFile}
                    />
                  </Box>
                )}
              </Stack>
              <Stack
                sx={{
                  pr: 4,
                  pl: !visibleImage || filePathAfterCrop ? 4 : '52px',
                }}
              >
                {(touchedFields.content ||
                  dirtyFields.content ||
                  errors.content) && (
                  <Typography
                    variant="overline"
                    sx={{
                      pt: 2,
                      color: errors.content?.message
                        ? 'error.main'
                        : 'text.secondary',
                    }}
                  >
                    {watchContent.length}/200
                  </Typography>
                )}
              </Stack>
              <Stack
                direction="row"
                flex={1}
                justifyContent="space-between"
                alignItems="flex-end"
                sx={{
                  pl: visibleImage ? 2 : 4,
                  pb: 1,
                  pr: 2,
                }}
              >
                {visibleImage && (
                  <IconButton.Image
                    onClick={handleChoseFile}
                    size="medium"
                    color={canChooseImage ? 'default' : 'inherit'}
                    disabled={!canChooseImage}
                  />
                )}
                <TextInputCell
                  type="text"
                  error={errors?.content}
                  {...register('content')}
                  textFieldProps={{
                    sx: {
                      p: 0,
                      pb: 2,
                      m: 0,
                      bgcolor: 'transparent',
                      width: '100%',
                    },
                    multiline: true,
                    minRows: 1,
                    autoFocus,
                    maxRows: 5,
                    variant: 'standard',
                    helperText: '',
                    InputProps: {
                      sx: {
                        border: 'none',
                        px: 0,
                        pt: 0,
                        bgcolor: 'grey.50',
                        '& textarea': {
                          cursor: canEditComment ? 'auto' : 'default',
                        },
                      },
                      disableUnderline: true,
                      readOnly: !canEditComment,
                    },
                  }}
                />
                <IconButton.Send
                  type="submit"
                  size="medium"
                  onClick={handleLoginModal()}
                  disabled={isLoggedIn ? !isValid || isSubmitting : false}
                  color={color}
                />
              </Stack>
            </Form>
          </Box>
        </BottomSafeArea>
        <ImageEditModal isConsole={isConsole} {...ModalProps} />
      </OrganismFrame>
    )
  }
)

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('postDetail')
    return (
      <CellFrame sx={{ width: '100%' }}>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)
